import { faAngleDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import isNil from 'lodash/isNil';
import React, { useState } from 'react';
import { css, styled } from 'styled-components';
import useTracking, { TrackingEventData } from '../../../hooks/useTracking';
import { BORDER_GRAY, FOCUS_OUTLINE, WHITE } from '../../../theme';
import { EventNames, EventSubCategories } from '../../../utils/trackingEvents';
import { SizeProp } from '../util';

const StyledSummary = styled.button<{ $size: SizeProp }>`
  position: sticky;
  top: 0;
  z-index: 1;

  display: flex;
  align-items: center;
  width: 100%;

  border: none;
  border-top: 1px solid ${BORDER_GRAY};
  background: inherit;
  border-radius: 8px;
  cursor: pointer;

  &:focus-visible {
    outline: ${FOCUS_OUTLINE};
  }

  ${({ $size }) => {
    switch ($size) {
      case SizeProp.Small: {
        return css`
          padding: 4px;
          gap: 4px;
        `;
      }
      case SizeProp.Medium:
      default: {
        return css`
          padding: 12px;
          gap: 8px;
        `;
      }
    }
  }}
`;

const IconWrapper = styled.div<{ $isOpen: boolean }>`
  transition: all 0.2s ease-in-out;
  ${({ $isOpen }) =>
    !$isOpen
      ? css`
          transform: rotate(-90deg);
        `
      : ``}
`;

const StyledAccordion = styled.div`
  background: white;
  border: 1px solid ${BORDER_GRAY};
  border-top: none;
  border-radius: 8px;
`;

const StyledDetails = styled.div<{ $isOpen: boolean }>`
  background-color: ${WHITE};

  overflow: hidden;
  flex-shrink: 0;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;

  ${({ $isOpen }) =>
    !$isOpen
      ? css`
          height: 0;
          visibility: hidden;
        `
      : ``}
`;

interface AccordionProps extends React.PropsWithChildren {
  label: React.ReactNode;
  children: React.ReactNode;
  open?: boolean;
  onChange?: (isExpanding: boolean) => void;
  size?: SizeProp;
  trackingData?: TrackingEventData;
  name?: string;
}

export const Accordion: React.FC<AccordionProps> = ({
  label,
  children,
  open,
  onChange,
  size = SizeProp.Medium,
  trackingData,
  name,
}) => {
  const { track } = useTracking();
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = !isNil(open);
  const isOpen = isControlled ? open : internalOpen;

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const nextState = !isOpen;

    if (trackingData) {
      track(EventNames.CaretClick, {
        ...trackingData,
        event_subcategory: nextState
          ? EventSubCategories.Expand
          : EventSubCategories.Collapse,
      });
    }

    if (onChange) {
      onChange(nextState);
    }
    if (!isControlled) {
      setInternalOpen(nextState);
    }
  };

  return (
    <StyledAccordion data-testid="accordion">
      <StyledSummary
        data-testid="accordion-Summary"
        onClick={handleClick}
        $size={size}
      >
        <IconWrapper $isOpen={isOpen}>
          <FontAwesomeIcon
            fixedWidth
            icon={faAngleDown}
            fontSize={
              {
                [SizeProp.Small]: '12px',
                [SizeProp.Medium]: '16px',
              }[size]
            }
          />
        </IconWrapper>
        {label}
      </StyledSummary>
      <StyledDetails
        data-testid={name ? `${name}-details` : `details`}
        $isOpen={isOpen}
      >
        {children}
      </StyledDetails>
    </StyledAccordion>
  );
};
